import { useState } from 'react';
import { FileText } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { ApiError } from '../lib/api';

type Mode = 'login' | 'register';

const ERRORS: Record<string, string> = {
  invalid_credentials: 'Email o password non corretti.',
  email_taken: 'Email già registrata.',
  validation: 'Controlla i campi: email valida e password di almeno 8 caratteri.',
};

export function LoginScreen() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === 'login') await login(email, password);
      else await register(email, password, name || undefined);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'error';
      setError(ERRORS[code] ?? 'Qualcosa è andato storto. Riprova.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="shell">
      <div className="glow glow--1" aria-hidden />
      <div className="glow glow--2" aria-hidden />
      <main className="shell__main">
        <div className="screen" style={{ maxWidth: 420, paddingTop: 64, paddingBottom: 40 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
            <span className="iconchip iconchip--solid">
              <FileText size={20} />
            </span>
            <div>
              <div className="eyebrow">Archivio Documentale</div>
              <h1 className="h1" style={{ fontSize: 24 }}>
                {mode === 'login' ? 'Accedi' : 'Crea account'}
              </h1>
            </div>
          </div>

          <form onSubmit={submit}>
            {mode === 'register' && (
              <div className="field">
                <label className="field__label" htmlFor="name">
                  Nome
                </label>
                <input
                  id="name"
                  className="field__input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Mario Rossi"
                  autoComplete="name"
                />
              </div>
            )}
            <div className="field">
              <label className="field__label" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                className="field__input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="mario.rossi@example.it"
                autoComplete="email"
                required
              />
            </div>
            <div className="field">
              <label className="field__label" htmlFor="password">
                Password
              </label>
              <input
                id="password"
                className="field__input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === 'register' ? 'Almeno 8 caratteri' : '••••••••'}
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                required
              />
            </div>

            {error && (
              <div className="alert" style={{ marginBottom: 16 }} role="alert">
                <span>{error}</span>
              </div>
            )}

            <button className="btn btn--accent btn--block" type="submit" disabled={busy}>
              {busy ? 'Attendere…' : mode === 'login' ? 'Accedi' : 'Registrati'}
            </button>
          </form>

          <p style={{ textAlign: 'center', marginTop: 20, color: 'var(--muted)', fontSize: 14 }}>
            {mode === 'login' ? 'Non hai un account?' : 'Hai già un account?'}{' '}
            <button
              type="button"
              onClick={() => {
                setMode(mode === 'login' ? 'register' : 'login');
                setError(null);
              }}
              style={{ background: 'none', border: 'none', color: 'var(--accent)', fontWeight: 600, padding: 0 }}
            >
              {mode === 'login' ? 'Registrati' : 'Accedi'}
            </button>
          </p>
        </div>
      </main>
    </div>
  );
}
